import { Flex, Box, Text } from "rebass";
import styled from "styled-components";
import { maxWidth, size, space, overflow, display } from "styled-system";
import { theme } from "../styles/theme";
import { ParallaxProvider, Parallax } from "react-scroll-parallax";
import { Arrow } from "./arrow";
import { Button } from "./button";
import { DynamicNextLink } from "./dynamic-next-link";
import { LazyImage } from "./lazy-image";

const TimelinePreview = ({ text, button_text, nodes, onButtonClick }) => {
  const DesktopNodes = () =>
    nodes.map((item, index) => {
      const hasDetailPage = !(item.timeline_node.data.detail_page === "No");

      const year = parseInt(item.timeline_node.data.date);

      const link = {
        internal: true,
        href: `/timeline-detail?uid=${item.timeline_node.uid}`,
        as: `/timeline/${item.timeline_node.uid}`
      };

      return (
        <NodeContainer
          justifyContent={index % 2 === 0 ? "flex-end" : "flex-start"}
          key={index}
        >
          <Year fontSize={["4em", "4em", "6em"]} fontFamily="boldItalic">
            {year}
          </Year>
          <ParallaxProvider>
            <Content tabIndex={!hasDetailPage ? 0 : ""}>
              {item.timeline_node.data.image && (
                <>
                  <Parallax y={[50, 0]}>
                    <DynamicNextLink link={hasDetailPage && link}>
                      <S_LazyImage
                        src={item.timeline_node.data.image.url}
                        alt={item.timeline_node.data.image.alt}
                      />
                      <Caption fontSize={1} fontFamily="medium">
                        {item.timeline_node.data.title}
                      </Caption>
                    </DynamicNextLink>
                  </Parallax>
                </>
              )}
            </Content>
          </ParallaxProvider>
        </NodeContainer>
      );
    });

  const MobileNodes = () =>
    nodes.map((node, i) => {
      const year = parseInt(node.timeline_node.data.date);
      const even = i % 2 === 0;
      return (
        <Flex
          key={i}
          mt={i > 0 ? 4 : 0}
          justifyContent={even ? "flex-end" : "flex-start"}
        >
          <Text width={8 / 10} textAlign={even ? "left" : "right"}>
            <Text
              mb={-15}
              ml={even ? -15 : 0}
              mr={!even ? -15 : 0}
              fontSize={40}
              fontFamily={"boldItalic"}
              letterSpacing={0.36}
            >
              {year}
            </Text>
            <LazyImage
              src={node.timeline_node.data.image.url}
              alt={node.timeline_node.data.image.alt}
            />
            <Text fontSize={1} fontFamily="medium">
              {node.timeline_node.data.title}
            </Text>
          </Text>
        </Flex>
      );
    });

  return (
    <Preview pb={[4, 4, 4, 6]} overflow="hidden">
      <DesktopNodesWrap display={["none", "none", "block"]}>
        <DesktopNodes />
      </DesktopNodesWrap>
      <MobileNodesWrap display={["block", "block", "none"]}>
        <MobileNodes />
      </MobileNodesWrap>
      <Box mt={[4, 4, 0]} px={[3, 3, 3, 4]}>
        <Inner mx="auto" maxWidth={[0]} textAlign="center">
          {text && (
            <Text mb={[4, 4, 5]} fontSize={[2, 2, 3]} lineHeight={1.5}>
              {text}
            </Text>
          )}
          <Button
            text={button_text}
            link={{ internal: true, href: "/timeline", as: `/timeline` }}
            variant="white"
          />
        </Inner>
        <StyledArrow
          ariaLabel="Scroll down to next section"
          mt={[6, 7, 8]}
          mx="auto"
          size={50}
          onClick={() => {
            onButtonClick();
          }}
        />
      </Box>
    </Preview>
  );
};

const Preview = styled(Box)`
  ${overflow}
`;

const Caption = styled(Text)`
  opacity: 0;
  transition: all 0.2s ease-in;
  position: relative;
  top: 10px;

  @media screen and (min-width: ${theme.breakpoints[1]}) {
    display: block;
  }
`;

const StyledArrow = styled(Arrow)`
  ${size}
  ${space}
  text-align: center;
`;

const Inner = styled(Text)`
  ${maxWidth}
`;

const Year = styled(Text)`
  position: absolute;
  top: -70px;
  left: -20px;
`;

const MobileNodesWrap = styled(Box)`
  ${display}
`;

const DesktopNodesWrap = styled(Box)`
  ${display}
  position: relative;
  min-height: 1150px;
`;

const NodeContainer = styled(Box)`
  position: absolute;

  img {
    height: auto;
    object-fit: contain;
    object-position: left;
    max-height: 300px;
  }

  &:first-child {
    left: 43.5%;
    top: 70px;
    width: 45.1%;
  }

  &:nth-child(2) {
    left: 14.5%;
    top: 173px;
    width: 21.6%;
  }

  &:nth-child(3) {
    left: 4.8%;
    top: 532px;
    width: 31.3%;
  }

  &:nth-child(4) {
    left: 77%;
    top: 668px;
    width: 23.6%;
  }

  &:nth-child(5) {
    left: 41%;
    top: 750px;
    width: 30.9%;
  }
`;

const Content = styled(Box)`
  position: relative;

  a {
    display: block;
  }

  &:hover,
  &:focus-within {
    img {
      transform: none;
    }
  }

  @media screen and (min-width: ${theme.breakpoints[1]}) {
    &:hover,
    &:focus-within {
      ${Caption} {
        opacity: 1;
      }
    }
  }
`;

const S_LazyImage = styled(LazyImage)`
  display: block;
  transition: all 0.2s ease-out;
`;

export { TimelinePreview };
